import { useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Download, KeyRound, ShieldCheck } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { toast } from "sonner";
import { encode } from "uqr";
import { authClient } from "../lib/auth-client";
import { useSession } from "../lib/session";
import { Button } from "./ui/button";
import { Badge, Card, CardHeader } from "./ui/card";
import { Dialog, DialogContent, DialogFooter } from "./ui/dialog";
import { Field, Input } from "./ui/input";

/** QR code as plain SVG rectangles (no innerHTML). */
export function QrCode({ value, size = 184 }: { value: string; size?: number }) {
  const qr = useMemo(() => encode(value, { ecc: "M", border: 2 }), [value]);
  const cells: string[] = [];
  qr.data.forEach((row, y) => {
    row.forEach((dark, x) => {
      if (dark) cells.push(`M${x} ${y}h1v1h-1z`);
    });
  });
  return (
    <svg
      viewBox={`0 0 ${qr.size} ${qr.size}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
      role="img"
      aria-label="QR-Code für die Authenticator-App"
      className="rounded-lg border border-zinc-200 bg-white"
    >
      <rect width={qr.size} height={qr.size} fill="#fff" />
      <path d={cells.join("")} fill="#111827" />
    </svg>
  );
}

function BackupCodes({ codes }: { codes: string[] }) {
  const [copied, setCopied] = useState(false);
  const text = codes.join("\n");
  return (
    <div className="space-y-3">
      <p className="text-sm text-zinc-600">
        Bewahre diese Codes sicher auf (z. B. im Passwort-Manager). Mit jedem Code kannst du dich
        einmal anmelden, falls du keinen Zugriff auf die App hast. Sie werden nur jetzt angezeigt.
      </p>
      <ul className="grid grid-cols-2 gap-1.5 rounded-lg bg-zinc-50 p-3 font-mono text-sm">
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={async () => {
            await navigator.clipboard.writeText(text);
            setCopied(true);
          }}
        >
          {copied ? <Check /> : <Copy />} {copied ? "Kopiert" : "Kopieren"}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            const url = URL.createObjectURL(new Blob([`${text}\n`], { type: "text/plain" }));
            const link = document.createElement("a");
            link.href = url;
            link.download = "wiederherstellungscodes.txt";
            link.click();
            URL.revokeObjectURL(url);
          }}
        >
          <Download /> Als Datei
        </Button>
      </div>
    </div>
  );
}

type Step =
  | { kind: "password"; action: "enable" | "disable" | "codes" }
  | { kind: "scan"; totpURI: string; backupCodes: string[] }
  | { kind: "codes"; backupCodes: string[] };

/** Card on the account page: set up, renew or switch off the authenticator app. */
export function TwoFactorCard() {
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const enabled = Boolean(
    (session?.user as { twoFactorEnabled?: boolean | null } | undefined)?.twoFactorEnabled,
  );
  const [step, setStep] = useState<Step | null>(null);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setStep(null);
    setPassword("");
    setCode("");
    setError(null);
  };
  const refreshSession = () => queryClient.invalidateQueries({ queryKey: ["session"] });

  async function submitPassword(event: FormEvent) {
    event.preventDefault();
    if (step?.kind !== "password") return;
    setBusy(true);
    setError(null);
    try {
      if (step.action === "enable") {
        const { data, error: enableError } = await authClient.twoFactor.enable({ password });
        if (enableError || !data || !("totpURI" in data) || !data.totpURI) throw enableError;
        setStep({ kind: "scan", totpURI: data.totpURI, backupCodes: data.backupCodes ?? [] });
      } else if (step.action === "codes") {
        const { data, error: codesError } = await authClient.twoFactor.generateBackupCodes({
          password,
        });
        if (codesError || !data) throw codesError;
        setStep({ kind: "codes", backupCodes: data.backupCodes });
      } else {
        const { error: disableError } = await authClient.twoFactor.disable({ password });
        if (disableError) throw disableError;
        await refreshSession();
        toast.success("Zwei-Faktor-Anmeldung ist aus");
        close();
      }
      setPassword("");
    } catch (caught) {
      const status = (caught as { status?: number } | null)?.status;
      setError(
        status === 400 || status === 401
          ? "Das Passwort ist falsch."
          : status === 403
            ? "Bitte melde dich aus Sicherheitsgründen neu an und versuche es dann erneut."
            : "Das hat nicht geklappt. Bitte versuche es noch einmal.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function confirmCode(event: FormEvent) {
    event.preventDefault();
    if (step?.kind !== "scan") return;
    setBusy(true);
    setError(null);
    const { error: verifyError } = await authClient.twoFactor.verifyTotp({
      code: code.replace(/\s+/g, ""),
    });
    setBusy(false);
    if (verifyError) {
      setError("Der Code stimmt nicht. Prüfe die Uhrzeit auf deinem Handy und versuche es erneut.");
      return;
    }
    await refreshSession();
    toast.success("Zwei-Faktor-Anmeldung ist aktiv");
    setStep({ kind: "codes", backupCodes: step.backupCodes });
    setCode("");
  }

  const secret = step?.kind === "scan" ? new URL(step.totpURI).searchParams.get("secret") : null;

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            Zwei-Faktor-Anmeldung
            {enabled ? <Badge tone="success">Aktiv</Badge> : <Badge>Aus</Badge>}
          </span>
        }
        description="Zusätzlich zum Passwort fragt die Anmeldung einen Code aus einer Authenticator-App ab (z. B. Google Authenticator, 1Password, Aegis). Für den Admin-Bereich ist sie Pflicht."
      />
      <div className="flex flex-wrap gap-2 p-5">
        {enabled ? (
          <>
            <Button
              variant="secondary"
              onClick={() => setStep({ kind: "password", action: "codes" })}
            >
              <KeyRound /> Neue Wiederherstellungscodes
            </Button>
            <Button
              variant="ghost"
              className="text-red-600 hover:bg-red-50"
              onClick={() => setStep({ kind: "password", action: "disable" })}
            >
              Ausschalten
            </Button>
          </>
        ) : (
          <Button onClick={() => setStep({ kind: "password", action: "enable" })}>
            <ShieldCheck /> Einrichten
          </Button>
        )}
      </div>

      <Dialog open={step !== null} onOpenChange={(open) => !open && close()}>
        {step?.kind === "password" ? (
          <DialogContent
            title={
              step.action === "enable"
                ? "Zwei-Faktor-Anmeldung einrichten"
                : step.action === "codes"
                  ? "Neue Wiederherstellungscodes"
                  : "Zwei-Faktor-Anmeldung ausschalten"
            }
            description={
              step.action === "codes"
                ? "Die bisherigen Codes werden ungültig."
                : "Bitte bestätige mit deinem Passwort."
            }
          >
            <form onSubmit={submitPassword} className="space-y-4">
              <Field label="Passwort" error={error}>
                <Input
                  type="password"
                  autoComplete="current-password"
                  autoFocus
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
              <DialogFooter>
                <Button type="button" variant="secondary" onClick={close}>
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  loading={busy}
                  variant={step.action === "disable" ? "danger" : "primary"}
                >
                  Weiter
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        ) : step?.kind === "scan" ? (
          <DialogContent
            title="App verbinden"
            description="Scanne den QR-Code mit deiner Authenticator-App und gib den angezeigten Code ein."
          >
            <form onSubmit={confirmCode} className="space-y-4">
              <div className="flex flex-col items-center gap-2">
                <QrCode value={step.totpURI} />
                {secret ? (
                  <p className="text-center text-xs text-zinc-500">
                    Oder von Hand eingeben:
                    <br />
                    <code className="font-mono text-zinc-800 select-all">{secret}</code>
                  </p>
                ) : null}
              </div>
              <Field label="Code aus der App" error={error}>
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                />
              </Field>
              <DialogFooter>
                <Button type="submit" loading={busy}>
                  Bestätigen
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        ) : step?.kind === "codes" ? (
          <DialogContent title="Wiederherstellungscodes">
            <BackupCodes codes={step.backupCodes} />
            <DialogFooter>
              <Button onClick={close}>Ich habe die Codes gespeichert</Button>
            </DialogFooter>
          </DialogContent>
        ) : null}
      </Dialog>
    </Card>
  );
}
